import * as React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The document direction, kept in sync with i18n: `src/i18n.ts` sets
 * `document.documentElement.dir` on `languageChanged` before any component
 * handler runs, so reading it here is always current.
 *
 * Needed wherever a Radix primitive takes a physical side (popover/tooltip
 * `side`) that must follow the layout's logical start/end, and for Radix's
 * DirectionProvider.
 */
export function useDocumentDirection(): 'ltr' | 'rtl' {
    const { i18n } = useTranslation();
    const [dir, setDir] = React.useState<'ltr' | 'rtl'>(() =>
        document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'
    );

    React.useEffect(() => {
        if (!i18n?.on) return;
        const update = () => setDir(document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr');
        i18n.on('languageChanged', update);
        return () => {
            i18n.off('languageChanged', update);
        };
    }, [i18n]);

    return dir;
}
